import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import { Download } from 'lucide-react'
import { DetailPage } from '@/components/templates/detail-page'
import { ListPage, type ListPageColumn, type ListPageTable } from '@/components/templates/list-page'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { getJSON } from '@/lib/api'
import { displayShortDate } from '@/lib/dates'
import {
  PayoutListToolbar,
  payoutListPath,
  type PayoutStatusFilter,
} from '@/pages/PayoutsPanelFilters'

interface Payout {
  id: number
  processor_reference: string
  currency: string
  status:
    | 'Recorded'
    | 'Needs attention'
    | 'Needs mapping'
    | 'Ready for deposit'
    | 'Deposited'
    | 'Error'
    | 'Drift'
    | 'Action required'
  attention_reason: string | null
  arrived_at: string
  arrival_date: string
  payments_total: string
  payments_total_cents: number
  fees: string
  fees_cents: number
  amount: string
  amount_cents: number
}

interface PayoutsResponse {
  payouts: Payout[]
  summary?: { count: number; net: string | null; net_cents: number | null }
}

interface PayoutItem {
  kind: 'Payment' | 'Refund' | 'Chargeback'
  source_reference: string
  source_path: string
  contact: { id: number; name: string; path: string }
  invoice: { id: number; number: string; path: string }
  gross: string
  processing_fee: string
  platform_fee: string
  net: string
  net_cents: number
}

interface PayoutDetail extends Payout {
  refund_total: string
  chargeback_total: string
  processor_fee: string
  platform_fee: string
  items: PayoutItem[]
}

export function PayoutsPanel() {
  const [selectedPayoutId, setSelectedPayoutId] = React.useState<number | null>(null)
  const [statusFilter, setStatusFilter] = React.useState<PayoutStatusFilter>('all')
  const query = useQuery({
    queryKey: ['payouts', statusFilter],
    queryFn: () => getJSON<PayoutsResponse>(payoutListPath(statusFilter)),
    retry: false,
  })
  const detailQuery = useQuery({
    queryKey: ['payouts', selectedPayoutId],
    queryFn: () =>
      getJSON<{ payout: PayoutDetail }>(`/api/v1/payouts/${selectedPayoutId as number}`),
    enabled: selectedPayoutId !== null,
    retry: false,
  })

  if (selectedPayoutId !== null) {
    return (
      <PayoutDetailView
        payoutId={selectedPayoutId}
        query={detailQuery}
        onBack={() => setSelectedPayoutId(null)}
      />
    )
  }

  return (
    <ListPage<Payout>
      title="Payouts"
      description="Deposits from your processor · newest first"
      toolbar={
        <PayoutListToolbar
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          summary={query.data?.summary}
        />
      }
      table={payoutTable(query, (payout) => setSelectedPayoutId(payout.id), statusFilter !== 'all')}
    />
  )
}

function payoutTable(
  query: ReturnType<typeof useQuery<PayoutsResponse>>,
  onOpen: (payout: Payout) => void,
  filtered: boolean,
): ListPageTable<Payout> {
  return {
    rows: query.data?.payouts ?? [],
    columns: payoutColumns,
    getRowId: (payout) => payout.id,
    getRowLabel: (payout) => `Payout ${payout.processor_reference}`,
    onRowOpen: onOpen,
    state: query.isLoading ? 'loading' : query.isError ? 'error' : 'ready',
    loadingTitle: 'Loading Payouts',
    errorTitle: 'Payouts unavailable',
    emptyTitle: filtered ? 'No payouts match the selected status.' : 'No payouts yet.',
    emptyDescription: filtered
      ? 'Choose another status or clear the filter.'
      : 'Payouts appear when your processor sends money to the bank.',
    emptyVariant: 'quiet',
  }
}

function PayoutDetailView({
  payoutId,
  query,
  onBack,
}: {
  payoutId: number
  query: ReturnType<typeof useQuery<{ payout: PayoutDetail }>>
  onBack: () => void
}) {
  const payout = query.data?.payout
  const back = {
    backHref: '#',
    backLabel: 'Payouts',
    onBack,
  }

  if (query.isLoading) {
    return (
      <DetailPage title="Payout" {...back}>
        <div role="status" className="overflow-hidden rounded-md border border-border bg-card">
          <EmptyState title="Loading Payout" className="min-h-52" />
        </div>
      </DetailPage>
    )
  }

  if (query.isError || !payout) {
    return (
      <DetailPage title="Payout" {...back}>
        <div role="alert" className="overflow-hidden rounded-md border border-border bg-card">
          <EmptyState title="Payout unavailable" className="min-h-52" />
        </div>
      </DetailPage>
    )
  }

  return (
    <DetailPage
      title={payout.amount}
      subtitle={`Payout · ${formatArrivalDate(payout.arrival_date)}`}
      badge={<Badge variant={payoutStatusVariant(payout.status)}>{payout.status}</Badge>}
      actions={
        <Button className="gap-2" variant="outline" asChild>
          <a href={`/api/v1/payouts/${payoutId}/export`} download>
            <Download className="h-4 w-4" />
            Export CSV
          </a>
        </Button>
      }
      {...back}
    >
      {payout.attention_reason && (
        <p className="text-body-sm text-muted-foreground">{payout.attention_reason}</p>
      )}
      <PayoutDecomposition payout={payout} />
      <PayoutTransactions items={payout.items} />
    </DetailPage>
  )
}

function PayoutDecomposition({ payout }: { payout: PayoutDetail }) {
  const rows = [
    ['Payments', payout.payments_total],
    ['Refunds', negativeMoney(payout.refund_total)],
    ['Chargebacks', negativeMoney(payout.chargeback_total)],
    ['Processing fees', negativeMoney(payout.processor_fee)],
    ['Platform Fee', negativeMoney(payout.platform_fee)],
  ]

  return (
    <section className="mt-6">
      <h2 className="mb-2 text-body font-medium">What&apos;s inside</h2>
      <div className="overflow-hidden rounded-md border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Category</TableHead>
              <TableHead className="text-right">Net</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(([label, amount]) => (
              <TableRow key={label}>
                <TableCell>{label}</TableCell>
                <MoneyCell amount={amount} />
              </TableRow>
            ))}
            <TableRow className="border-t-2 font-semibold hover:bg-transparent">
              <TableCell>Deposit</TableCell>
              <MoneyCell amount={payout.amount} />
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </section>
  )
}

function PayoutTransactions({ items }: { items: PayoutItem[] }) {
  return (
    <section className="mt-6">
      <div className="mb-2 flex items-baseline gap-2">
        <h2 className="text-body font-medium">Transactions</h2>
        <span className="text-caption text-muted-foreground">
          {items.length} {items.length === 1 ? 'item' : 'items'}
        </span>
      </div>
      <div className="overflow-hidden rounded-md border border-border bg-card">
        {items.length === 0 ? (
          <EmptyState
            variant="quiet"
            title="No matched transactions"
            description="Resolve the attention reason before itemized transactions can be shown."
            className="min-h-40"
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Item</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Invoice</TableHead>
                {['Gross', 'Processing fee', 'Platform Fee', 'Net'].map((heading) => (
                  <TableHead key={heading} className="text-right">
                    {heading}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={`${item.kind}-${item.source_reference}`}>
                  <TableCell className="font-medium">
                    <a className="hover:underline" href={item.source_path}>
                      {item.kind} {item.source_reference}
                    </a>
                  </TableCell>
                  <TableCell>
                    <a className="text-muted-foreground hover:underline" href={item.contact.path}>
                      {item.contact.name}
                    </a>
                  </TableCell>
                  <TableCell>
                    <a className="text-muted-foreground hover:underline" href={item.invoice.path}>
                      {item.invoice.number}
                    </a>
                  </TableCell>
                  <MoneyCell amount={item.gross} />
                  <MoneyCell amount={item.processing_fee} />
                  <MoneyCell amount={item.platform_fee} />
                  <MoneyCell amount={item.net} className="font-medium" />
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </section>
  )
}

function MoneyCell({ amount, className = '' }: { amount: string; className?: string }) {
  return (
    <TableCell
      className={`text-right tabular-nums ${amount.startsWith('-') ? 'text-destructive' : ''} ${className}`}
    >
      {amount}
    </TableCell>
  )
}

function negativeMoney(amount: string) {
  if (Number.parseFloat(amount.replace(/[^0-9.-]/g, '')) === 0) return amount

  return amount.startsWith('-') ? amount : `-${amount}`
}
const payoutColumns: ListPageColumn<Payout>[] = [
  {
    id: 'arrived',
    header: 'Arrived',
    cell: (payout) => <span className="font-medium">{formatArrivalDate(payout.arrival_date)}</span>,
  },
  { id: 'status', header: 'Status', cell: (payout) => <PayoutStatus payout={payout} /> },
  {
    id: 'payments',
    header: 'Payments',
    headerClassName: 'text-right',
    className: 'text-right tabular-nums',
    cell: (payout) => payout.payments_total,
  },
  {
    id: 'fees',
    header: 'Fees',
    headerClassName: 'text-right',
    className: 'text-right tabular-nums',
    cell: (payout) => payout.fees,
  },
  {
    id: 'net',
    header: 'Net',
    headerClassName: 'text-right',
    className: 'text-right tabular-nums font-medium',
    cell: (payout) => payout.amount,
  },
]

function PayoutStatus({ payout }: { payout: Payout }) {
  return (
    <div>
      <Badge variant={payoutStatusVariant(payout.status)}>{payout.status}</Badge>
      {payout.attention_reason && (
        <p className="mt-1 text-caption text-muted-foreground">{payout.attention_reason}</p>
      )}
    </div>
  )
}

function payoutStatusVariant(status: Payout['status']) {
  if (
    status === 'Needs attention' ||
    status === 'Needs mapping' ||
    status === 'Error' ||
    status === 'Drift' ||
    status === 'Action required'
  )
    return 'warning'
  if (status === 'Ready for deposit') return 'neutral'
  return 'success'
}

const formatArrivalDate = displayShortDate
